import { useCallback, useState } from 'react';
import { useCreateCoverAiRunMutation } from '../../../../redux/api/fymAPI';
import { useCoverEditor } from '../../CoverEditorContext';
import { coverAiRunError, type CoverAiRunError } from '../../doc/coverEditorErrors';

/** A reference picture of the AI request: an uploaded file of the artist. */
export interface CoverReference {
  fileId: number;
  url: string;
}

/** The most reference pictures of one request (spec §5). */
export const COVER_MAX_REFERENCES = 3;

/**
 * Starts an AI run of 4 pictures (`POST .../ai-runs`). With reference pictures and no confirmed
 * rights, it opens the rights sheet first; "Add the pictures" there sends the request with
 * `referenceRightsConfirmed`. A 400 `M24_REFERENCE_RIGHTS_REQUIRED` opens the sheet too (another
 * device may have a stale answer). `error` is what the panel shows for a refusal.
 */
export function useCoverAiRun() {
  const { accountId, catalogItemUuid, data, readOnly } = useCoverEditor();
  const [createRun, run] = useCreateCoverAiRunMutation();
  const [error, setError] = useState<CoverAiRunError | null>(null);
  const [isRightsOpen, setIsRightsOpen] = useState(false);
  const [pending, setPending] = useState<{ instructions: string; references: CoverReference[] } | null>(null);

  const send = useCallback(
    (instructions: string, references: CoverReference[], isRightsConfirmed: boolean) => {
      setError(null);
      createRun({
        accountId,
        catalogItemUuid,
        instructions: instructions.trim() === '' ? undefined : instructions.trim(),
        referenceFileIds: references.length === 0 ? undefined : references.map((reference) => reference.fileId),
        referenceRightsConfirmed: isRightsConfirmed ? true : undefined,
      })
        .unwrap()
        .then(() => setPending(null))
        .catch((caught: unknown) => {
          const kind = coverAiRunError(caught);
          if (kind === 'rights') {
            setPending({ instructions, references });
            setIsRightsOpen(true);
            return;
          }
          console.error('Cover editor: the AI run did not start', caught);
          setError(kind);
        });
    },
    [accountId, catalogItemUuid, createRun]
  );

  const generate = useCallback(
    (instructions: string, references: CoverReference[]) => {
      if (readOnly) {
        setError('demo');
        return;
      }
      if (references.length > 0 && !data.rightsConfirmed) {
        setPending({ instructions, references });
        setIsRightsOpen(true);
        return;
      }
      send(instructions, references, false);
    },
    [data.rightsConfirmed, readOnly, send]
  );

  const confirmRights = useCallback(() => {
    setIsRightsOpen(false);
    if (pending !== null) {
      send(pending.instructions, pending.references, true);
    }
  }, [pending, send]);

  const cancelRights = useCallback(() => {
    setIsRightsOpen(false);
    setPending(null);
  }, []);

  return { generate, isStarting: run.isLoading, error, isRightsOpen, confirmRights, cancelRights };
}
